import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserCirclePlus, ArrowClockwise, Copy, Sparkle, Broom } from "@phosphor-icons/react";

export default function InviteAndReplenish() {
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [replenBusy, setReplenBusy] = useState(false);
  const [simBusy, setSimBusy] = useState(false);
  const [simScale, setSimScale] = useState(5000);
  const [simYears, setSimYears] = useState(2);
  const [simStats, setSimStats] = useState(null);

  const load = async () => {
    const { data } = await api.get("/users?limit=1000");
    setPending(data.filter((u) => u.auth_provider === "sso_pending"));
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const invite = async (all = false) => {
    setBusy(true);
    try {
      const payload = all ? {} : { user_ids: Array.from(selected) };
      const { data } = await api.post("/admin/users/bulk-invite", payload);
      setInvites(data.invites || []);
      toast.success(`Invited ${data.count} user(s)`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyAll = () => {
    const text = invites.map((i) => `${i.email}: ${i.temp_password}`).join("\n");
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const replenish = async () => {
    if (!window.confirm("Run monthly credit replenishment now? This uses the current rollover policy in Settings.")) return;
    setReplenBusy(true);
    try {
      const { data } = await api.post("/admin/replenish-credits");
      toast.success(`Replenished ${data.replenished} wallets (policy: ${data.policy}, net added: ${data.total_added})`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setReplenBusy(false); }
  };

  const runSim = async () => {
    if (!window.confirm(`Generate ${simScale} demo employees with ${simYears} years of history? Any prior simulation data will be replaced.`)) return;
    setSimBusy(true);
    setSimStats(null);
    try {
      const { data } = await api.post("/admin/simulate-enterprise", { scale: simScale, years: simYears }, { timeout: 240000 });
      setSimStats(data);
      toast.success(`Generated ${data.users_created} users, ${data.tasks_created} tasks in ${data.duration_seconds}s`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Simulation failed"); }
    finally { setSimBusy(false); }
  };

  const clearSim = async () => {
    if (!window.confirm("Delete ALL simulated employees and their tasks/rewards? Real seed users are safe.")) return;
    setSimBusy(true);
    try {
      const { data } = await api.post("/admin/simulate-enterprise/clear");
      toast.success(`Removed ${data.users_removed} sim users, ${data.tasks_removed} sim tasks`);
      setSimStats(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSimBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="invite-replenish-page">
      {/* Enterprise Simulator */}
      <section className="border border-[#E1252A]/40 bg-[#E1252A]/5 p-5 space-y-3" data-testid="simulator-section">
        <div className="flex items-start gap-3">
          <Sparkle size={22} className="text-[#E1252A] mt-1" weight="fill" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold tracking-tight">Enterprise data simulator</h3>
            <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
              Generate a realistic organization: employees across levels, departments, skills, hierarchy, and years of task history
              — completed work, credits earned, badges awarded, applications. Perfect for stakeholder demos.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Employees</Label>
            <Input type="number" min={50} max={20000} value={simScale} onChange={(e) => setSimScale(Number(e.target.value))} className="rounded-sm w-32" data-testid="sim-scale-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Years of history</Label>
            <Input type="number" min={1} max={5} value={simYears} onChange={(e) => setSimYears(Number(e.target.value))} className="rounded-sm w-24" data-testid="sim-years-input" />
          </div>
          <Button onClick={runSim} disabled={simBusy} className="rounded-sm bg-foreground text-background" data-testid="sim-run-btn">
            <Sparkle size={14} className={`mr-1 ${simBusy ? "animate-spin" : ""}`} />
            {simBusy ? "Generating…" : "Generate"}
          </Button>
          <Button onClick={clearSim} disabled={simBusy} variant="outline" className="rounded-sm text-red-600 border-red-200" data-testid="sim-clear-btn">
            <Broom size={14} className="mr-1" /> Clear simulation
          </Button>
        </div>
        {simStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 pt-3 border-t border-[#E1252A]/30" data-testid="sim-stats">
            <SimStat label="Employees" value={simStats.users_created} />
            <SimStat label="Departments" value={simStats.departments_created} />
            <SimStat label="Tasks" value={simStats.tasks_created} />
            <SimStat label="Completed" value={simStats.completed_tasks} />
            <SimStat label="Applications" value={simStats.applications_created} />
            <SimStat label="Ledger tx" value={simStats.credit_transactions} />
            <SimStat label="Badges" value={simStats.badges_awarded} />
          </div>
        )}
      </section>

      {/* Credit replenishment card */}
      <section className="border border-border bg-background p-5" data-testid="replenish-section">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <ArrowClockwise size={22} className="text-[#E1252A] mt-1" />
            <div>
              <h3 className="text-lg font-semibold tracking-tight">Monthly credit replenishment</h3>
              <p className="text-xs text-zinc-500 mt-1 max-w-lg">
                Runs automatically the first time the app boots each calendar month.
                Manual trigger below uses the rollover policy set in Settings (reset / full / partial).
              </p>
            </div>
          </div>
          <Button onClick={replenish} disabled={replenBusy} className="rounded-sm bg-foreground text-background" data-testid="replenish-btn">
            <ArrowClockwise size={14} className={`mr-1 ${replenBusy ? "animate-spin" : ""}`} />
            {replenBusy ? "Running…" : "Run now"}
          </Button>
        </div>
      </section>

      {/* SSO invite card */}
      <section className="border border-border bg-background p-5 space-y-3" data-testid="invite-section">
        <div className="flex items-start gap-3">
          <UserCirclePlus size={22} className="text-[#E1252A]" />
          <div>
            <h3 className="text-lg font-semibold tracking-tight">Invite SSO-pending users</h3>
            <p className="text-xs text-zinc-500 mt-1">
              Users pulled from SAP/Workday/BambooHR/CSV plugins can't log in until they get a password.
              Generate temp passwords in bulk and share securely (e.g. via 1Password Send).
            </p>
          </div>
        </div>

        {pending.length === 0 ? (
          <div className="text-sm text-zinc-500 border border-dashed border-border p-8 text-center" data-testid="no-pending">
            No SSO-pending users. Great — everyone can sign in.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 pb-2 border-b border-border">
              <div className="text-xs uppercase tracking-wider text-zinc-500">{selected.size} of {pending.length} selected</div>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={() => invite(false)} disabled={busy || selected.size === 0} className="rounded-sm" data-testid="invite-selected-btn">Invite selected</Button>
                <Button size="sm" onClick={() => invite(true)} disabled={busy} className="rounded-sm bg-foreground text-background" data-testid="invite-all-btn">Invite all pending</Button>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    <th className="text-left py-2 px-3 w-8"></th>
                    <th className="text-left py-2 px-3">Name</th>
                    <th className="text-left py-2 px-3">Email</th>
                    <th className="text-left py-2 px-3 hidden md:table-cell">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((u) => (
                    <tr key={u.user_id} className="border-b border-border" data-testid={`pending-${u.user_id}`}>
                      <td className="py-2 px-3">
                        <Checkbox checked={selected.has(u.user_id)} onCheckedChange={() => toggle(u.user_id)} />
                      </td>
                      <td className="py-2 px-3">{u.name}</td>
                      <td className="py-2 px-3 text-zinc-500">{u.email}</td>
                      <td className="py-2 px-3 hidden md:table-cell">
                        {u.sso_source && <Badge variant="outline" className="rounded-sm text-[10px] uppercase font-mono">{u.sso_source}</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {invites.length > 0 && (
          <div className="mt-4 border border-[#E1252A]/40 bg-[#E1252A]/5 p-3" data-testid="invite-results">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.2em] text-[#E1252A] font-bold">Temp passwords — copy and share NOW, they won't be shown again</div>
              <Button size="sm" variant="outline" onClick={copyAll} className="rounded-sm" data-testid="copy-invites-btn"><Copy size={12} className="mr-1" /> Copy all</Button>
            </div>
            <div className="max-h-48 overflow-y-auto text-xs font-mono">
              {invites.map((i) => (
                <div key={i.user_id} className="flex items-center gap-3 py-1 border-b border-border last:border-0">
                  <span className="text-zinc-500 min-w-[220px] truncate">{i.email}</span>
                  <span className="text-[#E1252A] font-semibold">{i.temp_password}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function SimStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="text-lg font-bold font-mono text-[#E1252A]">{value}</div>
    </div>
  );
}
